import React from 'react';

function InvestmentsIndustrialTechnology() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/industrial-tech/factory-1140760_1280.jpg" alt="Industrial Technology" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Industrial Technology</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Automation, IoT, sustainability technologies, and digitalization transforming manufacturing and industrial operations.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Industrial Technology Investment Thesis</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We invest in companies digitizing and decarbonizing industrial operations through automation, data intelligence, and sustainable technologies.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">The Industrial Revolution 4.0</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Manufacturing and industrial sectors represent 16% of global GDP ($14T+) yet remain significantly under-digitized compared to consumer and enterprise software sectors. The convergence of IoT, AI, robotics, and sustainability mandates is driving unprecedented transformation—from predictive maintenance to lights-out manufacturing to circular economy models.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We target industrial technology companies solving high-ROI problems: reducing downtime, optimizing energy consumption, automating labor-intensive processes, and enabling compliance with environmental regulations. Our investments span manufacturing execution systems, industrial IoT platforms, robotics & automation, and clean-tech infrastructure.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-orange-700">Market Drivers:</strong> Industrial automation market projected to reach $395B by 2030. 75% of manufacturers cite digitalization as a top-3 priority, yet only 14% have scaled pilots to production.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/industrial-tech/ai-generated-7947639_1280.jpg" alt="Industrial tech thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-orange-600 opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Focus</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {t:'Automation & Robotics', d:'Collaborative robots, automated guided vehicles (AGVs), vision systems, pick-and-place solutions, and robotic process automation for industrial environments.', img:'/images/industrial-tech/engineer-4915794_1280.jpg'},
              {t:'Industrial IoT & Data', d:'Sensor networks, edge computing, digital twins, predictive maintenance platforms, and manufacturing analytics unlocking operational intelligence.', img:'/images/industrial-tech/soldering-7897827_1280.jpg'},
              {t:'Sustainability Tech', d:'Energy management systems, carbon tracking, waste reduction, renewable energy integration, and circular economy platforms driving net-zero operations.', img:'/images/industrial-tech/solar-8244680_1280.jpg'}
            ].map(x => (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <img src={x.img} alt={x.t} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{x.t}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {[
              {title: 'Smart Factory & MES', desc: 'Manufacturing execution systems, production scheduling, quality management, and factory operating systems connecting shop floor to top floor.'},
              {title: 'Supply Chain Intelligence', desc: 'Inventory optimization, demand forecasting, supply chain visibility, and logistics automation for industrial goods and components.'}
            ].map(item => (
              <div key={item.title} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Investment Approach</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We combine deep industrial domain expertise with technology assessment to identify scalable, high-impact solutions.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Investment Criteria</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-orange-600 pl-5 bg-orange-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Proven ROI & Adoption</h4>
                  <p className="text-sm text-gray-600">&lt;12 month payback periods with reference customers in production. Demonstrated value through OEE improvements, cost reduction, or quality gains.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Horizontal Scalability</h4>
                  <p className="text-sm text-gray-600">Solutions applicable across industries (discrete vs. process) and geographies. Platform architecture enabling rapid deployment and customization.</p>
                </div>
                <div className="border-l-4 border-orange-600 pl-5 bg-orange-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Integration & Interoperability</h4>
                  <p className="text-sm text-gray-600">Works with existing brownfield equipment, legacy systems (SCADA, ERP, PLCs), and industry standards (OPC UA, MQTT).</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Recurring Revenue Model</h4>
                  <p className="text-sm text-gray-600">SaaS subscriptions, maintenance contracts, or consumption-based pricing with high gross margins (65%+) and strong customer retention.</p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <img src="/new-assets/pexels-rdne-8292847.jpg" alt="Industrial investment" className="w-full h-64 object-cover rounded-2xl shadow-lg" />
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl">
                <h4 className="font-semibold mb-4">Value Creation Focus</h4>
                <ul className="space-y-2 text-sm text-white/90">
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Channel partnerships with OEMs and system integrators</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Vertical expansion into adjacent industries</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Product roadmap & AI/ML feature development</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Geographic expansion (NA, EU, APAC)</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> M&A strategy for complementary technologies</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-16 bg-gradient-to-br from-orange-900 to-orange-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Portfolio Impact</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our industrial technology investments drive measurable operational improvements and sustainability outcomes.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '18%', label: 'Average OEE improvement'},
              {metric: '35%', label: 'Reduction in unplanned downtime'},
              {metric: '22%', label: 'Energy consumption decrease'},
              {metric: '2,500+', label: 'Factories using our portfolio technologies'}
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{item.metric}</div>
                <div className="text-sm text-white/80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Partner on Industrial Technology</h3>
          <p className="mt-2 text-gray-600">Scale your industrial tech solution with capital, domain expertise, and go-to-market support.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsIndustrialTechnology;


